import {
  C3D_CELL,
  C3D_COLS,
  C3D_ATTACK_COLS,
  C3D_IDLE_FRAMES,
  C3D_WALK_FRAMES,
  C3D_HAIR_COLORS,
  C3D_GARMENT_COLORS,
  c3dBodySheet,
  c3dHairSheet,
  c3dHairOffsetY,
  c3dTopSheet,
  c3dTopHasCombatSheet,
  c3dBottomSheet,
  c3dShoesSheet,
  c3dHatSheet,
  c3dMaskSheet,
  c3dMaskHidesHair,
  c3dTopOffsetY,
  c3dBottomOffsetY,
  c3dShoesOffsetY,
  c3dHatOffsetY,
  c3dBottomOverShoes,
  type CharacterAppearance,
} from "@ghostland/shared";
import { loadPartImage, newCanvas, recolorBodySkin } from "./c3dImage.js";

/**
 * Renderer-agnostic c3d compositor (no Three/Phaser). Flattens an appearance into one
 * 20x8 atlas canvas: body + clothing layers + hair, each recoloured and drawn per-cell
 * (clipped so an offset layer never bleeds into a neighbouring cell). Used by both the
 * Three.js billboard (in-world) and the DOM inventory preview.
 *
 * Z-order (back→front): body → bottom(jeans) → shoes → top(shirt) → hair → hat(cap).
 */

const ASSET_BASE = "/assets";

/** Recolour an opaque sheet by luminance onto a 3-stop [shadow,mid,light] ramp. */
export function recolorByLuminance(
  src: CanvasImageSource & { width: number; height: number },
  ramp: ReadonlyArray<readonly [number, number, number]>,
): HTMLCanvasElement {
  const canvas = newCanvas(src.width, src.height);
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(src, 0, 0);
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  const [lo, mid, hi] = ramp;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] < 128) continue;
    const L = (0.3 * d[i] + 0.59 * d[i + 1] + 0.11 * d[i + 2]) / 255;
    let c: readonly [number, number, number];
    if (L < 0.5) {
      const t = L / 0.5;
      c = [lo[0] + (mid[0] - lo[0]) * t, lo[1] + (mid[1] - lo[1]) * t, lo[2] + (mid[2] - lo[2]) * t];
    } else {
      const t = (L - 0.5) / 0.5;
      c = [mid[0] + (hi[0] - mid[0]) * t, mid[1] + (hi[1] - mid[1]) * t, mid[2] + (hi[2] - mid[2]) * t];
    }
    d[i] = c[0]; d[i + 1] = c[1]; d[i + 2] = c[2];
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/** Per-cell clipped blit of a layer sheet onto the 32-col atlas, nudged by offsetY. A layer
 *  with only 20 cols (no run frames) has its run region (cols 20-31) filled from its WALK
 *  frames (cols 8-19), so clothes/hair keep animating during a sprint until re-rendered. */
function blitLayer(
  ctx: CanvasRenderingContext2D,
  layer: CanvasImageSource,
  rows: number,
  offsetY: number,
): void {
  const layerCols = Math.round((layer as { width: number }).width / C3D_CELL);
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < C3D_COLS; col++) {
      let src = col;
      if (col >= layerCols) {
        if (col >= C3D_IDLE_FRAMES + C3D_WALK_FRAMES) src = col - C3D_WALK_FRAMES; // run → matching walk
        else continue;
      }
      const sx = src * C3D_CELL, dx = col * C3D_CELL, sy = row * C3D_CELL;
      ctx.save();
      ctx.beginPath();
      ctx.rect(dx, sy + offsetY, C3D_CELL, C3D_CELL);
      ctx.clip();
      ctx.drawImage(layer, sx, sy, C3D_CELL, C3D_CELL, dx, sy + offsetY, C3D_CELL, C3D_CELL);
      ctx.restore();
    }
  }
}

/** A full-atlas-sized canvas with one layer blitted into it (nudged by offsetY). */
function layerCanvas(layer: CanvasImageSource, w: number, h: number, rows: number, offsetY: number): HTMLCanvasElement {
  const c = newCanvas(w, h);
  blitLayer(c.getContext("2d")!, layer, rows, offsetY);
  return c;
}

/**
 * Hide hair that extends out of the hat UPWARD or to the LEFT/RIGHT — but NOT downward.
 * A worn hat caps the hair: hair flowing out below the hat (per column) stays visible;
 * hair above the hat, or sticking out beside it within the hat's vertical band, is hidden.
 * Uses the PER-COLUMN hat bottom (the cap is domed — the lowest hat pixel is at the side
 * brim, so a single per-cell bottom wrongly hid hair just under the dome). Mutates hairCanvas.
 */
function maskHairUnderHat(hairCanvas: HTMLCanvasElement, hatCanvas: HTMLCanvasElement, rows: number): void {
  const hc = hairCanvas.getContext("2d")!;
  const W = hairCanvas.width, H = hairCanvas.height;
  const hair = hc.getImageData(0, 0, W, H);
  const hat = hatCanvas.getContext("2d")!.getImageData(0, 0, W, H);
  const hd = hair.data, td = hat.data;
  const cols = Math.round(W / C3D_CELL);
  const colBottom = new Int16Array(C3D_CELL); // lowest hat row per column (this cell)
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const ox = col * C3D_CELL, oy = row * C3D_CELL;
      let top = -1, bottom = -1; // overall hat band in this cell
      colBottom.fill(-1);
      for (let x = 0; x < C3D_CELL; x++) {
        for (let y = C3D_CELL - 1; y >= 0; y--) {
          if (td[((oy + y) * W + (ox + x)) * 4 + 3] >= 128) {
            if (colBottom[x] < 0) colBottom[x] = y;
            if (bottom < 0 || y > bottom) bottom = y;
            if (top < 0 || y < top) top = y;
          }
        }
      }
      if (bottom < 0) continue; // no hat in this cell → leave hair as-is
      for (let y = 0; y < C3D_CELL; y++) {
        for (let x = 0; x < C3D_CELL; x++) {
          const i = ((oy + y) * W + (ox + x)) * 4;
          if (hd[i + 3] < 128) continue;       // no hair here
          if (y < top) { hd[i + 3] = 0; continue; }       // above the hat → hide
          if (y > bottom) continue;            // below the whole hat → keep
          // within the hat's vertical band: keep only if BELOW the hat in this column
          const cb = colBottom[x];
          if (cb >= 0 && y > cb) continue;     // hair below the hat here → keep
          hd[i + 3] = 0;                        // above/under hat in column, or beside it → hide
        }
      }
    }
  }
  hc.putImageData(hair, 0, 0);
}

/** Load + recolour a garment sheet (color 0 = neutral white master, drawn as-is). */
async function loadGarment(path: string | null, colorId: number): Promise<CanvasImageSource | null> {
  if (!path) return null;
  const img = await loadPartImage(`${ASSET_BASE}/${path}`);
  if (!img) return null;
  if (colorId > 0 && C3D_GARMENT_COLORS[colorId]) {
    return recolorByLuminance(img, C3D_GARMENT_COLORS[colorId].ramp);
  }
  return img;
}

/**
 * Flatten an appearance into a single atlas canvas. Returns null if the body sheet
 * won't load.
 */
export async function compositeAtlas(
  a: CharacterAppearance,
  hasWeapon = false,
  hasShield = false,
): Promise<HTMLCanvasElement | null> {
  const body = await loadPartImage(`${ASSET_BASE}/${c3dBodySheet(a.face)}`);
  if (!body) return null;
  const rows = Math.round(body.height / C3D_CELL);
  // always 32 cols (run region falls back per layer)
  const canvas = newCanvas(C3D_COLS * C3D_CELL, body.height);
  const ctx = canvas.getContext("2d")!;
  blitLayer(ctx, recolorBodySkin(body, a.skinTone), rows, 0);

  // clothing layers (back→front), each clipped per-cell + recoloured. Bottom vs shoes
  // order depends on the pant style: skinny jeans tuck behind the shoes, but baggy
  // workpants drape OVER the boots (overShoes), so the pant cuffs cover the boot tops.
  const bottom = await loadGarment(c3dBottomSheet(a.bottom), a.bottomColor);
  const shoes = await loadGarment(c3dShoesSheet(a.shoes), a.shoesColor);
  const drawBottom = () => { if (bottom) blitLayer(ctx, bottom, rows, c3dBottomOffsetY(a.bottom)); };
  const drawShoes = () => { if (shoes) blitLayer(ctx, shoes, rows, c3dShoesOffsetY(a.shoes)); };
  if (c3dBottomOverShoes(a.bottom)) { drawShoes(); drawBottom(); }
  else { drawBottom(); drawShoes(); }
  const top = await loadGarment(c3dTopSheet(a.top), a.topColor);
  if (top) blitLayer(ctx, top, rows, c3dTopOffsetY(a.top));

  // hair + mask + hat (topmost). Build hair/hat as full-atlas canvases so the hat can mask the
  // hair: a worn cap hides any hair sticking out above or beside it. A worn MASK draws between
  // them (over hair, under hat); a hideHair mask (balaclava) suppresses the hair layer entirely.
  const maskHidesHair = a.mask > 0 && c3dMaskHidesHair(a.mask);
  let hairSrc: CanvasImageSource | null = null;
  const hairPath = maskHidesHair ? null : c3dHairSheet(a.hair);
  if (hairPath) {
    const hairImg = await loadPartImage(`${ASSET_BASE}/${hairPath}`);
    if (hairImg) {
      hairSrc = a.hairColor > 0 && C3D_HAIR_COLORS[a.hairColor]
        ? recolorByLuminance(hairImg, C3D_HAIR_COLORS[a.hairColor].ramp)
        : hairImg;
    }
  }
  const maskSrc = await loadGarment(c3dMaskSheet(a.mask), a.maskColor);
  const hatSrc = await loadGarment(c3dHatSheet(a.hat), a.hatColor);

  const hairLayer = hairSrc ? layerCanvas(hairSrc, canvas.width, canvas.height, rows, c3dHairOffsetY(a.hair)) : null;
  const hatLayer = hatSrc ? layerCanvas(hatSrc, canvas.width, canvas.height, rows, c3dHatOffsetY(a.hat)) : null;
  if (hairLayer && hatLayer) maskHairUnderHat(hairLayer, hatLayer, rows);
  if (hairLayer) ctx.drawImage(hairLayer, 0, 0);
  if (maskSrc) blitLayer(ctx, maskSrc, rows, 0);
  if (hatLayer) ctx.drawImage(hatLayer, 0, 0);

  // held weapons (combat): the sword/shield layers have the body silhouette punched out where they
  // sit behind it, so a straight blit gives correct per-direction occlusion. Shield (offhand) under
  // sword (primary), matching the attack swing's order.
  if (hasShield) {
    const shield = await loadPartImage(`${ASSET_BASE}/c3d/held_shield.png`);
    if (shield) ctx.drawImage(shield, 0, 0);
  }
  if (hasWeapon) {
    const sword = await loadPartImage(`${ASSET_BASE}/c3d/held_sword.png`);
    if (sword) ctx.drawImage(sword, 0, 0);
  }

  return canvas;
}

/** The attack swing as a STANDALONE layered atlas (16 frames x 8 dir-rows), mirroring compositeAtlas:
 *  skin-remapped body → clothing layers → the sword/shield (their body-occlusion is baked into the
 *  layer). All attack layers are pre-aligned (same camera + foot offset), so each is a straight blit.
 *  Garment layers not yet rendered are simply skipped — the swing still plays, just without that piece.
 *  Returns null if the attack body sheet is missing (callers fall back to the normal billboard). */
function attackPath(idle: string | null): string | null {
  return idle ? idle.replace("c3d/", "c3d/attack_") : null;
}
export async function compositeAttackAtlas(
  a: CharacterAppearance,
  hasWeapon = true,
  hasShield = true,
): Promise<HTMLCanvasElement | null> {
  const body = await loadPartImage(`${ASSET_BASE}/c3d/attack_body.png`);
  if (!body) return null;
  const canvas = newCanvas(body.width, body.height); // 16*144 x 8*144
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(recolorBodySkin(body, a.skinTone), 0, 0);

  const drawGarment = async (path: string | null, colorId: number): Promise<void> => {
    if (!path) return;
    const img = await loadPartImage(`${ASSET_BASE}/${path}`);
    if (!img) return;
    ctx.drawImage(
      colorId > 0 && C3D_GARMENT_COLORS[colorId] ? recolorByLuminance(img, C3D_GARMENT_COLORS[colorId].ramp) : img,
      0, 0,
    );
  };
  const bottom = attackPath(c3dBottomSheet(a.bottom));
  const shoes = attackPath(c3dShoesSheet(a.shoes));
  if (c3dBottomOverShoes(a.bottom)) { await drawGarment(shoes, a.shoesColor); await drawGarment(bottom, a.bottomColor); }
  else { await drawGarment(bottom, a.bottomColor); await drawGarment(shoes, a.shoesColor); }
  // Only tops with rendered combat sheets get a shirt layer here; the rest (no attack_ sheet) skip it
  // rather than firing a 404 on every swing.
  if (c3dTopHasCombatSheet(a.top)) await drawGarment(attackPath(c3dTopSheet(a.top)), a.topColor);

  // hair (recoloured by the hair palette) then hat on top — same as compositeAtlas, minus the precise
  // hat-masking (good enough for the swing). Missing attack-hair sheets just skip (character bald).
  // A hideHair mask (balaclava) suppresses hair here too — no attack mask sheet yet, but hair
  // popping back mid-swing would break the "hair is covered" illusion worse than a bare head.
  const hairPath = a.mask > 0 && c3dMaskHidesHair(a.mask) ? null : attackPath(c3dHairSheet(a.hair));
  if (hairPath) {
    const hairImg = await loadPartImage(`${ASSET_BASE}/${hairPath}`);
    if (hairImg) ctx.drawImage(
      a.hairColor > 0 && C3D_HAIR_COLORS[a.hairColor] ? recolorByLuminance(hairImg, C3D_HAIR_COLORS[a.hairColor].ramp) : hairImg, 0, 0);
  }
  await drawGarment(attackPath(c3dHatSheet(a.hat)), a.hatColor);

  // weapons last: the sword/shield layers already have the body silhouette punched out where they
  // sit behind it, so a straight blit gives correct occlusion over body + clothes.
  // only bake the layers the character actually has equipped (shield = offhand, sword = primary)
  if (hasShield) {
    const shield = await loadPartImage(`${ASSET_BASE}/c3d/attack_shield.png`);
    if (shield) ctx.drawImage(shield, 0, 0);
  }
  if (hasWeapon) {
    const sword = await loadPartImage(`${ASSET_BASE}/c3d/attack_sword.png`);
    if (sword) ctx.drawImage(sword, 0, 0);
  }

  void C3D_ATTACK_COLS; // (atlas is 16 cols; dimensions come straight from the body sheet)
  return canvas;
}

/** The held BLOCK stance as a STANDALONE layered atlas (1 frame x 8 dir-rows, 144 cell) — mirrors
 *  compositeAttackAtlas but static. Drawn while the shield is raised (blocking). Returns null if the
 *  block body sheet is missing (callers just keep the normal billboard). */
function blockPath(idle: string | null): string | null {
  return idle ? idle.replace("c3d/", "c3d/block_") : null;
}
export async function compositeBlockAtlas(
  a: CharacterAppearance,
  hasWeapon = false,
  hasShield = false,
): Promise<HTMLCanvasElement | null> {
  const body = await loadPartImage(`${ASSET_BASE}/c3d/block_body.png`);
  if (!body) return null;
  const canvas = newCanvas(body.width, body.height); // 1*144 x 8*144
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(recolorBodySkin(body, a.skinTone), 0, 0);

  const drawGarment = async (path: string | null, colorId: number): Promise<void> => {
    if (!path) return;
    const img = await loadPartImage(`${ASSET_BASE}/${path}`);
    if (!img) return;
    ctx.drawImage(
      colorId > 0 && C3D_GARMENT_COLORS[colorId] ? recolorByLuminance(img, C3D_GARMENT_COLORS[colorId].ramp) : img,
      0, 0,
    );
  };
  const bottom = blockPath(c3dBottomSheet(a.bottom));
  const shoes = blockPath(c3dShoesSheet(a.shoes));
  if (c3dBottomOverShoes(a.bottom)) { await drawGarment(shoes, a.shoesColor); await drawGarment(bottom, a.bottomColor); }
  else { await drawGarment(bottom, a.bottomColor); await drawGarment(shoes, a.shoesColor); }
  // Only tops with rendered combat sheets get a shirt layer here; the rest skip it (no 404).
  if (c3dTopHasCombatSheet(a.top)) await drawGarment(blockPath(c3dTopSheet(a.top)), a.topColor);

  const hairPath = a.mask > 0 && c3dMaskHidesHair(a.mask) ? null : blockPath(c3dHairSheet(a.hair));
  if (hairPath) {
    const hairImg = await loadPartImage(`${ASSET_BASE}/${hairPath}`);
    if (hairImg) ctx.drawImage(
      a.hairColor > 0 && C3D_HAIR_COLORS[a.hairColor] ? recolorByLuminance(hairImg, C3D_HAIR_COLORS[a.hairColor].ramp) : hairImg, 0, 0);
  }
  await drawGarment(blockPath(c3dHatSheet(a.hat)), a.hatColor);

  if (hasShield) {
    const shield = await loadPartImage(`${ASSET_BASE}/c3d/block_shield.png`);
    if (shield) ctx.drawImage(shield, 0, 0);
  }
  if (hasWeapon) {
    const sword = await loadPartImage(`${ASSET_BASE}/c3d/block_sword.png`);
    if (sword) ctx.drawImage(sword, 0, 0);
  }
  return canvas;
}

/** The seated SIT pose as a STANDALONE layered atlas (1 frame x 8 dir-rows, 144 cell) — identical
 *  structure to compositeBlockAtlas, swapping the `block_` prefix for `sit_`. Returns null if the sit
 *  body sheet is missing (the art may not be generated yet), so callers keep the normal idle pose. */
function sitPath(idle: string | null): string | null {
  return idle ? idle.replace("c3d/", "c3d/sit_") : null;
}
export async function composeSitAtlas(
  a: CharacterAppearance,
  hasWeapon = false,
  hasShield = false,
): Promise<HTMLCanvasElement | null> {
  const body = await loadPartImage(`${ASSET_BASE}/c3d/sit_body.png`);
  if (!body) return null;
  const canvas = newCanvas(body.width, body.height); // 1*144 x 8*144
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(recolorBodySkin(body, a.skinTone), 0, 0);

  const drawGarment = async (path: string | null, colorId: number): Promise<void> => {
    if (!path) return;
    const img = await loadPartImage(`${ASSET_BASE}/${path}`);
    if (!img) return;
    ctx.drawImage(
      colorId > 0 && C3D_GARMENT_COLORS[colorId] ? recolorByLuminance(img, C3D_GARMENT_COLORS[colorId].ramp) : img,
      0, 0,
    );
  };
  const bottom = sitPath(c3dBottomSheet(a.bottom));
  const shoes = sitPath(c3dShoesSheet(a.shoes));
  if (c3dBottomOverShoes(a.bottom)) { await drawGarment(shoes, a.shoesColor); await drawGarment(bottom, a.bottomColor); }
  else { await drawGarment(bottom, a.bottomColor); await drawGarment(shoes, a.shoesColor); }
  // Sit has a sheet for EVERY top (carve + decal-transferred + bomber), so always try to draw it —
  // drawGarment is a no-op if the sheet is missing (unlike attack/block which gate on combat sheets).
  await drawGarment(sitPath(c3dTopSheet(a.top)), a.topColor);

  const hairPath = a.mask > 0 && c3dMaskHidesHair(a.mask) ? null : sitPath(c3dHairSheet(a.hair));
  if (hairPath) {
    const hairImg = await loadPartImage(`${ASSET_BASE}/${hairPath}`);
    if (hairImg) ctx.drawImage(
      a.hairColor > 0 && C3D_HAIR_COLORS[a.hairColor] ? recolorByLuminance(hairImg, C3D_HAIR_COLORS[a.hairColor].ramp) : hairImg, 0, 0);
  }
  await drawGarment(sitPath(c3dHatSheet(a.hat)), a.hatColor);

  if (hasShield) {
    const shield = await loadPartImage(`${ASSET_BASE}/c3d/sit_shield.png`);
    if (shield) ctx.drawImage(shield, 0, 0);
  }
  if (hasWeapon) {
    const sword = await loadPartImage(`${ASSET_BASE}/c3d/sit_sword.png`);
    if (sword) ctx.drawImage(sword, 0, 0);
  }
  return canvas;
}

/** The airborne JUMP pose as a STANDALONE layered atlas (1 frame x 8 dir-rows, 144 cell) — identical
 *  structure to composeSitAtlas, swapping the `sit_` prefix for `jump_`. The head/arm-attached layers
 *  (tops, hair, hat, weapons) were packed from the idle frame-0 art (the upper body doesn't move in the
 *  jump), so — like sit — every layer has a sheet and we always try to draw it. Returns null if the
 *  jump body sheet is missing (callers keep the normal billboard). */
function jumpPath(idle: string | null): string | null {
  return idle ? idle.replace("c3d/", "c3d/jump_") : null;
}
export async function composeJumpAtlas(
  a: CharacterAppearance,
  hasWeapon = false,
  hasShield = false,
): Promise<HTMLCanvasElement | null> {
  const body = await loadPartImage(`${ASSET_BASE}/c3d/jump_body.png`);
  if (!body) return null;
  const canvas = newCanvas(body.width, body.height); // 1*144 x 8*144
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(recolorBodySkin(body, a.skinTone), 0, 0);

  const drawGarment = async (path: string | null, colorId: number): Promise<void> => {
    if (!path) return;
    const img = await loadPartImage(`${ASSET_BASE}/${path}`);
    if (!img) return;
    ctx.drawImage(
      colorId > 0 && C3D_GARMENT_COLORS[colorId] ? recolorByLuminance(img, C3D_GARMENT_COLORS[colorId].ramp) : img,
      0, 0,
    );
  };
  const bottom = jumpPath(c3dBottomSheet(a.bottom));
  const shoes = jumpPath(c3dShoesSheet(a.shoes));
  if (c3dBottomOverShoes(a.bottom)) { await drawGarment(shoes, a.shoesColor); await drawGarment(bottom, a.bottomColor); }
  else { await drawGarment(bottom, a.bottomColor); await drawGarment(shoes, a.shoesColor); }
  // Jump has a sheet for EVERY top (reused from idle frame-0), so always try to draw it.
  await drawGarment(jumpPath(c3dTopSheet(a.top)), a.topColor);

  const hairPath = a.mask > 0 && c3dMaskHidesHair(a.mask) ? null : jumpPath(c3dHairSheet(a.hair));
  if (hairPath) {
    const hairImg = await loadPartImage(`${ASSET_BASE}/${hairPath}`);
    if (hairImg) ctx.drawImage(
      a.hairColor > 0 && C3D_HAIR_COLORS[a.hairColor] ? recolorByLuminance(hairImg, C3D_HAIR_COLORS[a.hairColor].ramp) : hairImg, 0, 0);
  }
  await drawGarment(jumpPath(c3dHatSheet(a.hat)), a.hatColor);

  if (hasShield) {
    const shield = await loadPartImage(`${ASSET_BASE}/c3d/jump_shield.png`);
    if (shield) ctx.drawImage(shield, 0, 0);
  }
  if (hasWeapon) {
    const sword = await loadPartImage(`${ASSET_BASE}/c3d/jump_sword.png`);
    if (sword) ctx.drawImage(sword, 0, 0);
  }
  return canvas;
}

// --- smoking idle overlay (Blunt buff) -------------------------------------

/** Per-direction cigarette vector from the mouth + whether the cig stub shows (face visible).
 *  Row order: 0 down,1 down-right,2 right,3 up-right,4 up,5 up-left,6 left,7 down-left. The cig
 *  DANGLES out to the facing side so it reads at this small scale; back views show smoke only. */
const SMOKE_CIG: ReadonlyArray<{ dx: number; dy: number; show: boolean }> = [
  { dx: 5, dy: 1, show: true },   // down
  { dx: 6, dy: 0, show: true },   // down-right
  { dx: 6, dy: 0, show: true },   // right (profile)
  { dx: 4, dy: 0, show: false },  // up-right (3/4 back)
  { dx: 0, dy: 0, show: false },  // up (back) — smoke from the crown
  { dx: -4, dy: 0, show: false }, // up-left (3/4 back)
  { dx: -6, dy: 0, show: true },  // left (profile)
  { dx: -5, dy: 1, show: true },  // down-left
];

let smokeAnchorCache: { mx: number; my: number; top: number }[][] | null = null;

/** Mouth/head anchors per (row, idle-frame), measured ONCE from the base body sheet. The body
 *  geometry is identical for every appearance (skin only recolours; hair/hat add pixels on top),
 *  so these anchors are appearance-independent — clothing/hair never shift the mouth. */
async function smokeAnchors(): Promise<{ mx: number; my: number; top: number }[][] | null> {
  if (smokeAnchorCache) return smokeAnchorCache;
  const img = await loadPartImage(`${ASSET_BASE}/c3d/body_none.png`);
  if (!img) return null;
  const c = newCanvas(img.width, img.height);
  const cx = c.getContext("2d", { willReadFrequently: true });
  if (!cx) return null;
  cx.drawImage(img, 0, 0);
  const d = cx.getImageData(0, 0, c.width, c.height).data, W = c.width, C = C3D_CELL;
  const table: { mx: number; my: number; top: number }[][] = [];
  for (let row = 0; row < 8; row++) {
    const frames: { mx: number; my: number; top: number }[] = [];
    for (let f = 0; f < C3D_IDLE_FRAMES; f++) {
      const ox = f * C, oy = row * C;
      let top = C, sumX = 0, n = 0;
      for (let y = 0; y < C; y++) for (let x = 0; x < C; x++) {
        if (d[((oy + y) * W + (ox + x)) * 4 + 3] > 40 && y < top) top = y;
      }
      for (let y = top; y < top + 18 && y < C; y++) for (let x = 0; x < C; x++) {
        if (d[((oy + y) * W + (ox + x)) * 4 + 3] > 40) { sumX += x; n++; }
      }
      frames.push({ mx: n ? Math.round(sumX / n) : C / 2, my: top + 16, top });
    }
    table.push(frames);
  }
  smokeAnchorCache = table;
  return table;
}

/**
 * Build the looping standing-smoke atlas from an ALREADY-composited body atlas: the player's own
 * idle frames (body + clothing + hair + skin, exactly as the normal idle) with a lit cigarette at
 * the mouth and an animated rising smoke wisp drawn on top. 8 idle frames x 8 direction rows.
 * Returns null until the base-body anchors have loaded.
 */
export async function composeSmokeAtlas(composited: CanvasImageSource): Promise<HTMLCanvasElement | null> {
  const anchors = await smokeAnchors();
  if (!anchors) return null;
  const C = C3D_CELL, F = C3D_IDLE_FRAMES;
  const out = newCanvas(C * F, C * 8);
  const ctx = out.getContext("2d");
  if (!ctx) return null;
  ctx.imageSmoothingEnabled = false;
  for (let row = 0; row < 8; row++) {
    const cig = SMOKE_CIG[row];
    for (let f = 0; f < F; f++) {
      const ox = f * C, oy = row * C;
      // the player's normal composited idle frame (clothing/hair/skin intact)
      ctx.drawImage(composited, f * C, row * C, C, C, ox, oy, C, C);
      // clamp drawing to this cell so the cigarette/smoke never bleed into a neighbour
      const put = (x: number, y: number, r: number, g: number, b: number, a: number): void => {
        if (x < ox || x >= ox + C || y < oy || y >= oy + C || a <= 0) return;
        ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
        ctx.fillRect(x, y, 1, 1);
      };
      const a = anchors[row][f], mx = ox + a.mx, my = oy + a.my;
      let ex = mx + cig.dx, ey = my + cig.dy;
      if (!cig.show && row === 4) { ex = mx; ey = oy + a.top + 1; } // straight back → smoke from crown
      if (cig.show) {
        const steps = Math.max(Math.abs(cig.dx), Math.abs(cig.dy), 1);
        for (let s = 0; s <= steps; s++) {
          const lx = Math.round(mx + (cig.dx * s) / steps), ly = Math.round(my + (cig.dy * s) / steps);
          put(lx, ly, 236, 232, 220, 1);          // cream paper
          put(lx, ly + 1, 198, 192, 178, 1);       // underside shade → 2px thick
        }
        put(ex, ey, 255, 122, 40, 1);              // ember
        put(ex - Math.sign(cig.dx || 1), ey, 120, 60, 30, 1); // ash band behind the ember
        put(ex, ey - 1, 255, 210, 100, 0.86);      // hot core glow
      } else {
        put(ex, ey, 255, 122, 40, 0.82);           // faint ember peeking from behind/above
      }
      // rising smoke plume — scrolls up 1px/frame (seamless over 8 frames), curling + fading.
      const sx = ex + Math.sign(cig.dx || 0), H = 14;
      for (let r = 0; r < H; r++) {
        const y = ey - 2 - r;
        const x = sx + Math.round(2.2 * Math.sin(r * 0.6 + (f * Math.PI) / 4));
        const al = Math.max(0, 0.95 * (1 - r / (H + 2)));
        put(x, y, 232, 234, 240, al);
        if (r < H - 3) put(x + 1, y, 220, 222, 230, al * (((r + f) % 2) ? 0.59 : 0.9));
        if (r < 6) put(x - 1, y, 220, 222, 230, al * 0.78);
      }
    }
  }
  return out;
}
